import { Capacitor } from '@capacitor/core';
import { useState, useEffect, useCallback } from 'react';

import { USBDetection } from '@/plugins/USBDetection/index'; // Adjust import path as needed
import {
  USB_EVENTS,
  USBStateChangeEvent,
  USBFileSelectedEvent,
  USBDetectionErrorType,
  QSenseConfig,
} from '@/plugins/USBDetection/types';

interface UseUSBDetectionReturn {
  isConnected: boolean;
  devices: USBStateChangeEvent['devices'];
  selectedFile: string | null;
  error: string | null;
  writeFileHeader: (config: QSenseConfig) => Promise<void>;
  checkConnection: () => Promise<void>;
  checkFile: () => Promise<void>;
  clearError: () => void;
}

// TODO: Fix proper error handling when error is triggered multiple times by the Plugin
export const useUSBDetection = (): UseUSBDetectionReturn => {
  const [isConnected, setIsConnected] = useState(false);
  const [devices, setDevices] = useState<USBStateChangeEvent['devices']>([]);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleError = (err: any) => {
    let errorMessage = 'An unexpected error occurred';

    if (typeof err === 'string') {
      errorMessage = err;
    } else if (err?.code) {
      switch (err.code) {
        case USBDetectionErrorType.MISSING_FILE_PATH:
          errorMessage = 'Please connect a USB device and select a file';
          break;
        case USBDetectionErrorType.INVALID_URL:
          errorMessage = 'The selected file location is invalid. Please try selecting the file again';
          break;
        case USBDetectionErrorType.SECURITY_ACCESS_DENIED:
          errorMessage = 'Permission denied to access the USB file. Please ensure the file is accessible';
          break;
        case USBDetectionErrorType.DOCUMENT_PICKER_ERROR:
          errorMessage = 'Could not open file picker. Please try again';
          break;
        case USBDetectionErrorType.VIEW_CONTROLLER_UNAVAILABLE:
          errorMessage = 'Could not open file picker. Please restart the app';
          break;
        default:
          errorMessage = err.message || 'An unexpected error occurred';
      }
    }

    setError(errorMessage);
  };

  // TODO: Change this to documentPicker to select the file
  const checkConnection = useCallback(async () => {
    try {
      if (Capacitor.isNativePlatform()) {
        const result = await USBDetection.checkUSBConnection();
        setIsConnected(result.isConnected);
        setDevices(result.devices);
      }
    } catch (err) {
      handleError(err);
    }
  }, []);

  // Check if the user still has access to the file
  const checkFile = useCallback(async () => {
    try {
      if (Capacitor.isNativePlatform()) {
        await USBDetection.checkFile();
      }
    } catch (err) {
      handleError(err);
    }
  }, []);

  const writeFileHeader = useCallback(async (config: QSenseConfig) => {
    try {
      if (Capacitor.isNativePlatform()) {
        await USBDetection.createFileHeader(config);
      }
    } catch (err) {
      handleError(err);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    // Set up USB state change listener
    const stateChangeListener = USBDetection.addListener(USB_EVENTS.STATE_CHANGE, (event: USBStateChangeEvent) => {
      setIsConnected(event.isConnected);
      setDevices(event.devices);
    });

    // Set up file selection listener
    const fileSelectedListener = USBDetection.addListener(USB_EVENTS.FILE_SELECTED, (event: USBFileSelectedEvent) => {
      setSelectedFile(event.files);
    });

    return () => {
      stateChangeListener.remove();
      fileSelectedListener.remove();
    };
  }, []);

  return {
    isConnected,
    devices,
    selectedFile,
    error,
    checkConnection,
    checkFile,
    clearError,
    writeFileHeader,
  };
};
